import { createClient } from '@supabase/supabase-js';
import { logger } from './logger';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn('Supabase credentials not configured. Running in offline mode.');
}

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        storageKey: 'ja-drivers-guide-auth',
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  : null;

// Helper to check if Supabase is configured
export const isSupabaseConfigured = () => !!supabase;

// ==================== AUTH HELPERS ====================
export const auth = {
  signUp: async (email, password, metadata = {}) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata }
    });
  },

  signIn: async (email, password) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.auth.signInWithPassword({ email, password });
  },

  signOut: async () => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.auth.signOut();
  },

  getUser: async () => {
    if (!supabase) return { data: { user: null } };
    return await supabase.auth.getUser();
  },

  getSession: async () => {
    if (!supabase) return { data: { session: null } };
    return await supabase.auth.getSession();
  },

  onAuthStateChange: (callback) => {
    if (!supabase) return { data: { subscription: { unsubscribe: () => {} } } };
    return supabase.auth.onAuthStateChange(callback);
  }
};

// ==================== DATABASE HELPERS ====================
export const db = {
  // ==================== DEPRECATED - Use serviceProviders instead ====================
  // These legacy table functions are kept for backwards compatibility only.
  // All new code should use db.serviceProviders.getByType('lawyer'), etc.
  // The data has been migrated to the unified service_providers table.

  // @deprecated - Use db.serviceProviders.getByType('lawyer') instead
  lawyers: {
    getAll: async () => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('lawyers').select('*').eq('is_active', true).order('name');
    },
    getById: async (id) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('lawyers').select('*').eq('id', id).single();
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('lawyers').insert(data).select().single();
    },
    update: async (id, data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('lawyers').update(data).eq('id', id).select().single();
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('lawyers').delete().eq('id', id);
    }
  },

  // @deprecated - Use db.serviceProviders.getByType('insurance') instead
  insuranceCompanies: {
    getAll: async () => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('insurance_companies').select('*').eq('is_active', true).order('name');
    },
    getById: async (id) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('insurance_companies').select('*').eq('id', id).single();
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('insurance_companies').insert(data).select().single();
    },
    update: async (id, data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('insurance_companies').update(data).eq('id', id).select().single();
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('insurance_companies').delete().eq('id', id);
    }
  },

  // @deprecated - Use db.serviceProviders.getByType('evaluator') instead
  vehicleEvaluators: {
    getAll: async () => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('vehicle_evaluators').select('*').eq('is_active', true).order('name');
    },
    getById: async (id) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('vehicle_evaluators').select('*').eq('id', id).single();
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('vehicle_evaluators').insert(data).select().single();
    },
    update: async (id, data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('vehicle_evaluators').update(data).eq('id', id).select().single();
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('vehicle_evaluators').delete().eq('id', id);
    }
  },

  // ==================== END DEPRECATED ====================

  // Service Providers - UNIFIED TABLE for ALL provider types
  // Types: mechanic, wrecker, parts, dealership, lawyer, insurance, evaluator, other
  serviceProviders: {
    getAll: async (type = null) => {
      if (!supabase) return { data: [], error: null };
      let query = supabase.from('service_providers').select('*').eq('is_active', true).order('name');
      if (type) query = query.eq('type', type);
      return await query;
    },
    // Admin version - returns ALL providers including inactive
    getAllAdmin: async (type = null) => {
      if (!supabase) return { data: [], error: null };
      let query = supabase.from('service_providers').select('*').order('created_at', { ascending: false });
      if (type) query = query.eq('type', type);
      return await query;
    },
    getByType: async (type) => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('service_providers').select('*').eq('type', type).eq('is_active', true).order('name');
    },
    getByTypeAdmin: async (type) => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('service_providers').select('*').eq('type', type).order('created_at', { ascending: false });
    },
    getById: async (id) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('service_providers').select('*').eq('id', id).single();
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('service_providers').insert(data).select().single();
    },
    update: async (id, data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('service_providers').update(data).eq('id', id).select().single();
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('service_providers').delete().eq('id', id);
    },
    // Find all service providers linked to a user (by owner_id)
    // All provider types (mechanic, lawyer, insurance, evaluator, etc.) are now in service_providers
    getByOwnerId: async (userId) => {
      if (!supabase) return { data: [], error: null };
      const { data, error } = await supabase
        .from('service_providers')
        .select('id, name, type, tier, is_active, logo_url, email, phone, parish')
        .eq('owner_id', userId);

      if (error) return { data: [], error };

      // Add providerTable for compatibility with VendorPortal
      const providers = (data || []).map(p => ({ ...p, providerTable: 'service_providers' }));
      return { data: providers, error: null };
    }
  },

  // Business Submissions (Get Listed)
  submissions: {
    getAll: async (status = null) => {
      if (!supabase) return { data: [], error: null };
      let query = supabase.from('business_submissions').select('*').order('created_at', { ascending: false });
      if (status) query = query.eq('status', status);
      return await query;
    },
    getById: async (id) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('business_submissions').select('*').eq('id', id).single();
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      // Don't use .select() - regular users only have INSERT permission, not SELECT
      return await supabase.from('business_submissions').insert({ ...data, status: 'pending' });
    },
    approve: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({ status: 'approved', reviewed_at: new Date().toISOString() }).eq('id', id).select().single();
    },
    reject: async (id, reason = '') => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({ status: 'rejected', rejection_reason: reason, reviewed_at: new Date().toISOString() }).eq('id', id).select().single();
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').delete().eq('id', id);
    },
    resetToPending: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({ status: 'pending', reviewed_at: null }).eq('id', id).select().single();
    },
    verifyPayment: async (id, verifiedBy = null) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({
        payment_verified: true,
        payment_verified_by: verifiedBy,
        payment_verified_at: new Date().toISOString()
      }).eq('id', id).select().single();
    }
  },

  // Hotspots
  hotspots: {
    getAll: async () => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('hotspots').select('*').eq('is_active', true).order('created_at', { ascending: false });
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('hotspots').insert(data).select().single();
    },
    confirm: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.rpc('increment_hotspot_confirmations', { hotspot_id: id });
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('hotspots').delete().eq('id', id);
    }
  },

  // Speed Cameras
  speedCameras: {
    getAll: async () => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('speed_cameras').select('*').eq('is_active', true).order('created_at', { ascending: false });
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('speed_cameras').insert(data).select().single();
    },
    confirm: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.rpc('increment_camera_confirmations', { camera_id: id });
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('speed_cameras').delete().eq('id', id);
    }
  },

  // User Profiles
  profiles: {
    get: async (userId) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('profiles').select('*').eq('id', userId).single();
    },
    update: async (userId, data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('profiles').update(data).eq('id', userId).select().single();
    },
    getAll: async () => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    },
    setRole: async (userId, role) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('profiles').update({ role }).eq('id', userId).select().single();
    },
    suspend: async (userId, suspended = true) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('profiles').update({ is_suspended: suspended }).eq('id', userId).select().single();
    },
    delete: async (userId) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      // Delete profile - auth user must be deleted separately via Supabase dashboard
      return await supabase.from('profiles').delete().eq('id', userId);
    }
  },

  // Analytics / Stats
  stats: {
    getDashboard: async () => {
      if (!supabase) return { data: null, error: null };
      // Lawyers/insurance/evaluators live in the consolidated
      // service_providers table (typed rows), not the legacy tables.
      const [users, lawyers, insurance, evaluators, serviceProviders, submissions, hotspots, cameras] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('service_providers').select('id', { count: 'exact', head: true }).eq('type', 'lawyer'),
        supabase.from('service_providers').select('id', { count: 'exact', head: true }).eq('type', 'insurance'),
        supabase.from('service_providers').select('id', { count: 'exact', head: true }).eq('type', 'evaluator'),
        supabase.from('service_providers').select('id', { count: 'exact', head: true }),
        supabase.from('business_submissions').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('hotspots').select('id', { count: 'exact', head: true }),
        supabase.from('speed_cameras').select('id', { count: 'exact', head: true })
      ]);
      return {
        data: {
          totalUsers: users.count || 0,
          totalLawyers: lawyers.count || 0,
          totalInsurance: insurance.count || 0,
          totalEvaluators: evaluators.count || 0,
          totalServiceProviders: serviceProviders.count || 0,
          pendingSubmissions: submissions.count || 0,
          totalHotspots: hotspots.count || 0,
          totalCameras: cameras.count || 0
        },
        error: null
      };
    }
  }
};

// ==================== STORAGE HELPERS ====================
export const storage = {
  uploadImage: async (bucket, path, file) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: false
    });
  },
  getPublicUrl: (bucket, path) => {
    if (!supabase) return null;
    return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
  },
  // For private buckets: mint a short-lived signed URL (default 1 hour).
  getSignedUrl: async (bucket, path, expiresIn = 3600) => {
    if (!supabase) return { data: null, error: { message: 'Supabase not configured' } };
    return await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
  },
  deleteImage: async (bucket, path) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.storage.from(bucket).remove([path]);
  }
};

// ==================== BANNER ADS ====================
export const bannerAds = {
  getActive: async (page = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('banner_ads')
      .select('*')
      .eq('is_active', true)
      .or('start_date.is.null,start_date.lte.' + new Date().toISOString().split('T')[0])
      .or('end_date.is.null,end_date.gte.' + new Date().toISOString().split('T')[0])
      .order('priority', { ascending: false });

    if (page) {
      query = query.contains('pages', [page]);
    }
    return await query;
  },
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.from('banner_ads').select('*').order('created_at', { ascending: false });
  },
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('banner_ads').select('*').eq('id', id).single();
  },
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ads').insert(data).select().single();
  },
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ads').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).select().single();
  },
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ads').delete().eq('id', id);
  },
  getByAdvertiser: async (advertiserId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase.from('banner_ads').select('*').eq('advertiser_id', advertiserId).order('created_at', { ascending: false });
  }
};

// ==================== LISTING TIERS ====================
export const listingTiers = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.from('listing_tiers').select('*').eq('is_active', true).order('sort_order');
  },
  getByName: async (name) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('listing_tiers').select('*').eq('name', name).single();
  }
};

// ==================== APP SETTINGS ====================
export const appSettings = {
  get: async (key) => {
    if (!supabase) return { data: null, error: null };
    try {
      const { data, error } = await supabase.from('app_settings').select('value').eq('key', key).single();
      if (error) return { data: null, error: null }; // Return null if setting doesn't exist
      return { data: data?.value, error: null };
    } catch {
      return { data: null, error: null };
    }
  },
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.from('app_settings').select('*').order('key');
  },
  set: async (key, value, description = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('app_settings').upsert({
      key,
      value,
      description,
      updated_at: new Date().toISOString()
    }, { onConflict: 'key' });
  }
};

// ==================== DATA CORRECTIONS ====================
export const dataCorrections = {
  submit: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('data_corrections').insert(data).select().single();
  },
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase.from('data_corrections').select('*').order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  },
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('data_corrections').select('*').eq('id', id).single();
  },
  updateStatus: async (id, status, adminNotes = null, reviewerId = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = {
      status,
      admin_notes: adminNotes,
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    if (status === 'fixed') {
      updates.fixed_at = new Date().toISOString();
    }
    return await supabase.from('data_corrections').update(updates).eq('id', id).select().single();
  },
  getStats: async () => {
    if (!supabase) return { data: null, error: null };
    const [pending, reviewing, verified, fixed, rejected] = await Promise.all([
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'reviewing'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'verified'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'fixed'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'rejected')
    ]);
    return {
      data: {
        pending: pending.count || 0,
        reviewing: reviewing.count || 0,
        verified: verified.count || 0,
        fixed: fixed.count || 0,
        rejected: rejected.count || 0,
        total: (pending.count || 0) + (reviewing.count || 0) + (verified.count || 0) + (fixed.count || 0) + (rejected.count || 0)
      },
      error: null
    };
  }
};

// ==================== ANALYTICS HELPERS ====================
export const analyticsDb = {
  getSummary: async (startDate, endDate, entityType = null, parish = null) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.rpc('get_analytics_summary', {
      p_start_date: startDate,
      p_end_date: endDate,
      p_entity_type: entityType,
      p_parish: parish
    });
  },
  getTopEntities: async (startDate, endDate, entityType, limit = 10) => {
    if (!supabase) return { data: [], error: null };
    return await supabase.rpc('get_top_entities', {
      p_start_date: startDate,
      p_end_date: endDate,
      p_entity_type: entityType,
      p_limit: limit
    });
  },
  getByParish: async (startDate, endDate) => {
    if (!supabase) return { data: [], error: null };
    return await supabase.rpc('get_analytics_by_parish', {
      p_start_date: startDate,
      p_end_date: endDate
    });
  },
  // Get top viewed pages
  getTopPages: async (startDate, endDate, limit = 10) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('analytics_events')
      .select('page')
      .eq('event_type', 'page_view')
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59')
      .not('page', 'is', null);

    if (error) return { data: [], error };

    // Group and count pages
    const pageCounts = {};
    (data || []).forEach(row => {
      pageCounts[row.page] = (pageCounts[row.page] || 0) + 1;
    });

    const sorted = Object.entries(pageCounts)
      .map(([page, count]) => ({ page, view_count: count }))
      .sort((a, b) => b.view_count - a.view_count)
      .slice(0, limit);

    return { data: sorted, error: null };
  },
  // Get top search terms
  getTopSearches: async (startDate, endDate, limit = 10) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('analytics_events')
      .select('metadata')
      .eq('event_type', 'search')
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59');

    if (error) return { data: [], error };

    // Extract and count search queries
    const searchCounts = {};
    (data || []).forEach(row => {
      const query = row.metadata?.query;
      if (query) {
        const normalizedQuery = query.toLowerCase().trim();
        searchCounts[normalizedQuery] = (searchCounts[normalizedQuery] || 0) + 1;
      }
    });

    const sorted = Object.entries(searchCounts)
      .map(([query, count]) => ({ query, search_count: count }))
      .sort((a, b) => b.search_count - a.search_count)
      .slice(0, limit);

    return { data: sorted, error: null };
  },
  // Get device breakdown
  getDeviceBreakdown: async (startDate, endDate) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('analytics_events')
      .select('device_type')
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59');

    if (error) return { data: [], error };

    const deviceCounts = { desktop: 0, mobile: 0, tablet: 0 };
    (data || []).forEach(row => {
      const device = row.device_type || 'desktop';
      deviceCounts[device] = (deviceCounts[device] || 0) + 1;
    });

    const total = Object.values(deviceCounts).reduce((a, b) => a + b, 0);
    const result = Object.entries(deviceCounts).map(([device, count]) => ({
      device,
      count,
      percentage: total > 0 ? ((count / total) * 100).toFixed(1) : 0
    }));

    return { data: result, error: null };
  },
  // Get referrer sources
  getReferrers: async (startDate, endDate, limit = 10) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('analytics_events')
      .select('referrer')
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59')
      .not('referrer', 'is', null)
      .neq('referrer', '');

    if (error) return { data: [], error };

    const refCounts = {};
    (data || []).forEach(row => {
      try {
        const url = new URL(row.referrer);
        const domain = url.hostname.replace('www.', '');
        refCounts[domain] = (refCounts[domain] || 0) + 1;
      } catch {
        refCounts['Direct'] = (refCounts['Direct'] || 0) + 1;
      }
    });

    const sorted = Object.entries(refCounts)
      .map(([source, count]) => ({ source, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);

    return { data: sorted, error: null };
  },
  // Get daily trend data
  getDailyTrend: async (startDate, endDate) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('analytics_events')
      .select('created_at, event_type')
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59')
      .order('created_at', { ascending: true });

    if (error) return { data: [], error };

    // Group by date
    const dailyData = {};
    (data || []).forEach(row => {
      const date = row.created_at.split('T')[0];
      if (!dailyData[date]) {
        dailyData[date] = { date, page_views: 0, events: 0 };
      }
      dailyData[date].events++;
      if (row.event_type === 'page_view') {
        dailyData[date].page_views++;
      }
    });

    return { data: Object.values(dailyData), error: null };
  },
  // Get raw events for detailed analysis
  getRecentEvents: async (limit = 50) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('analytics_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
  },
  // Get ad performance stats
  getAdPerformance: async (startDate, endDate) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('banner_ads')
      .select('id, name, advertiser_name, placement, impressions, clicks, ctr, is_active')
      .order('impressions', { ascending: false });
    return { data: data || [], error };
  },
  // Get top directory listings across all types
  getTopDirectoryListings: async (startDate, endDate, limit = 15) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('analytics_events')
      .select('entity_type, entity_id, entity_name')
      .in('event_type', ['directory_view', 'directory_click'])
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59')
      .not('entity_id', 'is', null);

    if (error) return { data: [], error };

    // Group by entity
    const entityStats = {};
    (data || []).forEach(row => {
      const key = `${row.entity_type}:${row.entity_id}`;
      if (!entityStats[key]) {
        entityStats[key] = {
          entity_type: row.entity_type,
          entity_id: row.entity_id,
          entity_name: row.entity_name,
          views: 0,
          clicks: 0
        };
      }
      entityStats[key].views++;
    });

    // Count clicks separately
    (data || []).forEach(row => {
      if (row.event_type === 'directory_click') {
        const key = `${row.entity_type}:${row.entity_id}`;
        if (entityStats[key]) {
          entityStats[key].clicks++;
        }
      }
    });

    const sorted = Object.values(entityStats)
      .sort((a, b) => b.views - a.views)
      .slice(0, limit);

    return { data: sorted, error: null };
  },
  // Get listing views by type
  getListingViewsByType: async (startDate, endDate) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('analytics_events')
      .select('entity_type')
      .eq('event_type', 'directory_view')
      .gte('created_at', startDate)
      .lte('created_at', endDate + 'T23:59:59')
      .not('entity_type', 'is', null);

    if (error) return { data: [], error };

    const typeCounts = {};
    (data || []).forEach(row => {
      typeCounts[row.entity_type] = (typeCounts[row.entity_type] || 0) + 1;
    });

    const result = Object.entries(typeCounts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);

    return { data: result, error: null };
  }
};

// ==================== SAVED OFFENCES ====================
export const savedOffences = {
  getAll: async (userId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('saved_offences')
      .select('*')
      .eq('user_id', userId)
      .order('saved_at', { ascending: false });
  },
  add: async (userId, offenceCode) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('saved_offences')
      .insert({ user_id: userId, offence_code: offenceCode })
      .select()
      .single();
  },
  remove: async (userId, offenceCode) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('saved_offences')
      .delete()
      .eq('user_id', userId)
      .eq('offence_code', offenceCode);
  },
  clear: async (userId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('saved_offences')
      .delete()
      .eq('user_id', userId);
  },
  sync: async (userId, offenceCodes) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // First clear existing, then insert new ones
    await supabase.from('saved_offences').delete().eq('user_id', userId);
    if (offenceCodes.length === 0) return { error: null };
    const records = offenceCodes.map(code => ({ user_id: userId, offence_code: code }));
    return await supabase.from('saved_offences').insert(records);
  }
};

// ==================== PAYMENT PROOFS ====================
export const paymentProofs = {
  getBySubmission: async (submissionId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_proofs')
      .select('*')
      .eq('submission_id', submissionId)
      .order('created_at', { ascending: false });
  },
  getByUser: async (userId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_proofs')
      .select('*, business_submissions(business_name, tier, status)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
  },
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('payment_proofs')
      .insert(data)
      .select()
      .single();
  },
  updateStatus: async (id, status, reviewerId = null, rejectionReason = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = {
      status,
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    if (rejectionReason) updates.rejection_reason = rejectionReason;
    return await supabase
      .from('payment_proofs')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
  },
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('payment_proofs')
      .select('*, business_submissions(business_name, tier, contact_email)')
      .order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  }
};

// ==================== USER NOTIFICATION PREFERENCES ====================
export const notificationPreferences = {
  get: async (userId) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('user_notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .single();
  },
  update: async (userId, preferences) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('user_notification_preferences')
      .upsert({
        user_id: userId,
        ...preferences,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
  }
};

// ==================== USER SUBMISSIONS (for dashboard) ====================
export const userSubmissions = {
  getByUser: async (userId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('business_submissions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
  },
  getByEmail: async (email) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('business_submissions')
      .select('*')
      .eq('contact_email', email)
      .order('created_at', { ascending: false });
  },
  linkToUser: async (submissionId, userId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('business_submissions')
      .update({ user_id: userId })
      .eq('id', submissionId)
      .select()
      .single();
  }
};

// ==================== ADMIN NOTIFICATIONS ====================
export const adminNotifications = {
  // Get all notifications (with optional filters)
  getAll: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('admin_notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.type) query = query.eq('type', filters.type);
    if (filters.is_read !== undefined) query = query.eq('is_read', filters.is_read);
    if (filters.limit) query = query.limit(filters.limit);

    return await query;
  },

  // Get unread count
  getUnreadCount: async () => {
    if (!supabase) return { count: 0, error: null };
    const { count, error } = await supabase
      .from('admin_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false);
    return { count: count || 0, error };
  },

  // Mark notification as read
  markAsRead: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .update({ is_read: true })
      .eq('id', id);
  },

  // Mark all as read
  markAllAsRead: async () => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .update({ is_read: true })
      .eq('is_read', false);
  },

  // Delete notification
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .delete()
      .eq('id', id);
  },

  // Create a new notification
  create: async ({ type, title, message, data = {} }) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .insert({
        type,
        title,
        message,
        data,
        is_read: false,
        created_at: new Date().toISOString()
      })
      .select()
      .single();
  },

  // Send email notification via Edge Function
  sendEmail: async (notification) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    try {
      const { data, error } = await supabase.functions.invoke('send-admin-notification', {
        body: notification
      });
      return { data, error };
    } catch (error) {
      logger.error('Error sending notification email:', error);
      return { data: null, error };
    }
  },

  // Subscribe to new notifications (real-time)
  subscribe: (callback) => {
    if (!supabase) return { unsubscribe: () => {} };
    const channel = supabase
      .channel('admin-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_notifications'
        },
        (payload) => {
          // UI-only: update the bell/list in real time. The notification email
          // is sent server-side by the on_admin_notification_send_email trigger
          // (see supabase/migrations/20260710_admin_notification_email_trigger.sql)
          // so it goes out at all times and exactly once — not from the browser.
          callback(payload.new);
        }
      )
      .subscribe();

    return {
      unsubscribe: () => supabase.removeChannel(channel)
    };
  }
};

// ==================== SUBSCRIPTIONS ====================
export const subscriptions = {
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('vendor_subscriptions')
      .select(`
        *,
        vendor:service_providers(id, name, email, phone, tier, is_active, logo_url)
      `)
      .order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  },

  getByVendorId: async (vendorId) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('vendor_subscriptions')
      .select('*')
      .eq('vendor_id', vendorId)
      .single();
  },

  create: async (vendorId, tier, billingPeriod = 'monthly', createdBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('create_vendor_subscription', {
      p_vendor_id: vendorId,
      p_tier: tier,
      p_billing_period: billingPeriod,
      p_created_by: createdBy
    });
  },

  activate: async (subscriptionId, paymentId = null, updatedBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('activate_subscription', {
      p_subscription_id: subscriptionId,
      p_payment_id: paymentId,
      p_updated_by: updatedBy
    });
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('vendor_subscriptions')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  suspend: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // First get the vendor_id
    const { data: sub } = await supabase.from('vendor_subscriptions').select('vendor_id').eq('id', id).single();
    if (sub) {
      // Deactivate the service provider
      await supabase.from('service_providers').update({ is_active: false }).eq('id', sub.vendor_id);
    }
    return await supabase
      .from('vendor_subscriptions')
      .update({ status: 'suspended', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  getUpcomingRenewals: async (days = 7) => {
    if (!supabase) return { data: [], error: null };
    return await supabase.rpc('get_upcoming_renewals', { p_days: days });
  },

  getGracePeriod: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.rpc('get_grace_period_subscriptions');
  },

  getDashboardStats: async () => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('subscription_dashboard_stats').select('*').single();
  }
};

// ==================== PAYMENTS ====================
export const payments = {
  getAll: async (vendorId = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('payment_transactions')
      .select(`
        *,
        vendor:service_providers(id, name, email),
        recorded_by_user:profiles!payment_transactions_recorded_by_fkey(full_name, email)
      `)
      .order('payment_date', { ascending: false });
    if (vendorId) query = query.eq('vendor_id', vendorId);
    return await query;
  },

  getByVendorId: async (vendorId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_transactions')
      .select('*')
      .eq('vendor_id', vendorId)
      .order('payment_date', { ascending: false });
  },

  record: async (vendorId, amount, paymentMethod, transactionRef = null, notes = null, recordedBy = null, activateSubscription = true) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('record_payment', {
      p_vendor_id: vendorId,
      p_amount: amount,
      p_payment_method: paymentMethod,
      p_transaction_ref: transactionRef,
      p_notes: notes,
      p_recorded_by: recordedBy,
      p_activate_subscription: activateSubscription
    });
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('payment_transactions').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('payment_transactions').update(data).eq('id', id).select().single();
  },

  getThisMonth: async () => {
    if (!supabase) return { data: [], error: null };
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    return await supabase
      .from('payment_transactions')
      .select('*')
      .gte('payment_date', startOfMonth.toISOString())
      .eq('status', 'completed');
  }
};

// ==================== INVOICES ====================
export const invoices = {
  getAll: async (vendorId = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('invoices')
      .select('*')
      .order('issued_date', { ascending: false });
    if (vendorId) query = query.eq('vendor_id', vendorId);
    return await query;
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('invoices').select('*').eq('id', id).single();
  },

  getByNumber: async (invoiceNumber) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('invoices').select('*').eq('invoice_number', invoiceNumber).single();
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // Generate invoice number
    const { data: invNum } = await supabase.rpc('generate_invoice_number');
    return await supabase.from('invoices').insert({ ...data, invoice_number: invNum }).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('invoices').update(data).eq('id', id).select().single();
  }
};

// ==================== EMAIL TEMPLATES ====================
export const emailTemplates = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.from('email_templates').select('*').order('name');
  },

  getByKey: async (templateKey) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('email_templates').select('*').eq('template_key', templateKey).single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('email_templates')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Helper to escape HTML entities to prevent XSS
  escapeHtml: (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  },

  // Render a template with variables (with XSS protection)
  render: (template, variables, escapeValues = true) => {
    let subject = template.subject;
    let bodyHtml = template.body_html;
    let bodyText = template.body_text;

    // Replace all {{variable}} placeholders with escaped values
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      // Escape HTML in values to prevent XSS attacks
      const safeValue = escapeValues ? emailTemplates.escapeHtml(value) : (value || '');
      subject = subject.replace(regex, safeValue);
      bodyHtml = bodyHtml.replace(regex, safeValue);
      if (bodyText) bodyText = bodyText.replace(regex, value || ''); // Plain text doesn't need escaping
    });

    return { subject, bodyHtml, bodyText };
  }
};

// ==================== NOTIFICATION LOG ====================
export const notificationLog = {
  getAll: async (vendorId = null, type = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('notification_log')
      .select('*')
      .order('sent_at', { ascending: false });
    if (vendorId) query = query.eq('vendor_id', vendorId);
    if (type) query = query.eq('notification_type', type);
    return await query;
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('notification_log').insert(data).select().single();
  }
};

// ==================== PAYMENT REQUESTS ====================
export const paymentRequests = {
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('payment_requests')
      .select(`
        *,
        vendor:service_providers(id, name, email, phone, tier),
        reviewed_by_user:profiles!payment_requests_reviewed_by_fkey(full_name, email)
      `)
      .order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  },

  getByVendorId: async (vendorId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_requests')
      .select('*')
      .eq('vendor_id', vendorId)
      .order('created_at', { ascending: false });
  },

  getPending: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_requests')
      .select(`
        *,
        vendor:service_providers(id, name, email, phone, tier)
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
  },

  approve: async (requestId, adminNotes = null, reviewedBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('approve_payment_request', {
      p_request_id: requestId,
      p_admin_notes: adminNotes,
      p_reviewed_by: reviewedBy
    });
  },

  reject: async (requestId, rejectionReason, reviewedBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('reject_payment_request', {
      p_request_id: requestId,
      p_rejection_reason: rejectionReason,
      p_reviewed_by: reviewedBy
    });
  },

  // Generate access token for a vendor
  generateAccessToken: async (vendorId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('generate_vendor_access_token', {
      p_vendor_id: vendorId
    });
  }
};

// ==================== BANNER AD PACKAGES ====================
export const bannerAdPackages = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_packages')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
  },

  getByPlacement: async (placement) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_packages')
      .select('*')
      .eq('placement', placement)
      .eq('is_active', true)
      .order('duration_months', { ascending: true });
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('banner_ad_packages')
      .select('*')
      .eq('id', id)
      .single();
  }
};

// ==================== BANNER AD REQUESTS ====================
export const bannerAdRequests = {
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('banner_ad_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  },

  getPending: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_requests')
      .select('*')
      .in('status', ['pending', 'payment_submitted', 'design_pending', 'design_review'])
      .order('created_at', { ascending: false });
  },

  getByEmail: async (email) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_requests')
      .select('*')
      .eq('contact_email', email)
      .order('created_at', { ascending: false });
  },

  getByServiceProvider: async (providerId, providerType) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_requests')
      .select('*')
      .eq('service_provider_id', providerId)
      .eq('service_provider_type', providerType)
      .order('created_at', { ascending: false });
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).select().single();
  },

  // Admin actions
  approveForPayment: async (id, adminNotes = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'awaiting_payment',
      admin_notes: adminNotes,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  verifyPayment: async (id, verifiedBy, adminNotes = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'payment_verified',
      payment_verified_by: verifiedBy,
      payment_verified_at: new Date().toISOString(),
      admin_notes: adminNotes,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  activate: async (id, bannerAdId, startDate, endDate) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'active',
      banner_ad_id: bannerAdId,
      actual_start_date: startDate,
      actual_end_date: endDate,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  reject: async (id, rejectionReason) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'rejected',
      rejection_reason: rejectionReason,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  // Vendor actions
  submitPaymentProof: async (id, paymentMethod, paymentReference, paymentProofUrl) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'payment_submitted',
      payment_method: paymentMethod,
      payment_reference: paymentReference,
      payment_proof_url: paymentProofUrl,
      payment_date: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  submitAdCreative: async (id, imageUrl, imageMobileUrl, linkUrl, altText) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'design_review',
      ad_image_url: imageUrl,
      ad_image_url_mobile: imageMobileUrl,
      ad_link_url: linkUrl,
      ad_alt_text: altText,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  }
};

// ==================== HERO CAROUSEL ====================
export const heroCarousel = {
  // Get carousel settings
  getSettings: async () => {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('hero_carousel_settings')
      .select('*')
      .limit(1)
      .single();
    return { data, error };
  },

  // Update carousel settings
  updateSettings: async (settings) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // Get the first (and only) settings row ID
    const { data: existing } = await supabase
      .from('hero_carousel_settings')
      .select('id')
      .limit(1)
      .single();

    if (existing?.id) {
      return await supabase
        .from('hero_carousel_settings')
        .update({ ...settings, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select()
        .single();
    }
    // If no settings exist, create them
    return await supabase
      .from('hero_carousel_settings')
      .insert(settings)
      .select()
      .single();
  },

  // Get all slides (admin)
  getAllSlides: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('hero_slides')
      .select('*')
      .order('sort_order', { ascending: true });
  },

  // Get active slides (public)
  getActiveSlides: async () => {
    if (!supabase) return { data: [], error: null };
    const now = new Date().toISOString();
    return await supabase
      .from('hero_slides')
      .select('*')
      .eq('is_active', true)
      .or(`start_date.is.null,start_date.lte.${now}`)
      .or(`end_date.is.null,end_date.gte.${now}`)
      .order('sort_order', { ascending: true });
  },

  // Get slide by ID
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('hero_slides')
      .select('*')
      .eq('id', id)
      .single();
  },

  // Create slide
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .insert(data)
      .select()
      .single();
  },

  // Update slide
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete slide
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .delete()
      .eq('id', id);
  },

  // Reorder slides
  reorder: async (slideIds) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = slideIds.map((id, index) =>
      supabase
        .from('hero_slides')
        .update({ sort_order: index })
        .eq('id', id)
    );
    const results = await Promise.all(updates);
    const failed = results.find((r) => r.error);
    return { error: failed ? failed.error : null };
  },

  // Toggle slide active status
  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  }
};

// ==================== ROAD FATALITY TRACKER ====================
export const roadFatalities = {
  // Get all verified accidents with optional filters
  getAll: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('road_accidents')
      .select(`
        *,
        victims:road_accident_victims(*),
        submitter:profiles!road_accidents_submitted_by_fkey(full_name)
      `)
      .eq('is_verified', true)
      .order('accident_date', { ascending: false });

    if (filters.year) query = query.gte('accident_date', `${filters.year}-01-01`).lte('accident_date', `${filters.year}-12-31`);
    if (filters.parish) query = query.eq('parish', filters.parish);
    if (filters.vehicleType) query = query.contains('vehicles_involved', [filters.vehicleType]);
    if (filters.limit) query = query.limit(filters.limit);

    return await query;
  },

  // Get accidents for admin (including unverified)
  getAllAdmin: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('road_accidents')
      .select(`
        *,
        victims:road_accident_victims(*),
        submitter:profiles!road_accidents_submitted_by_fkey(full_name),
        reviewer:profiles!road_accidents_reviewed_by_fkey(full_name)
      `)
      .order('created_at', { ascending: false });

    if (filters.status === 'pending') query = query.eq('is_verified', false).is('reviewed_at', null);
    if (filters.status === 'verified') query = query.eq('is_verified', true);
    if (filters.status === 'rejected') query = query.eq('is_verified', false).not('reviewed_at', 'is', null);
    if (filters.year) query = query.gte('accident_date', `${filters.year}-01-01`).lte('accident_date', `${filters.year}-12-31`);
    if (filters.parish) query = query.eq('parish', filters.parish);

    return await query;
  },

  // Get single accident by ID
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('road_accidents')
      .select(`
        *,
        victims:road_accident_victims(*),
        submitter:profiles!road_accidents_submitted_by_fkey(full_name, email),
        reviewer:profiles!road_accidents_reviewed_by_fkey(full_name)
      `)
      .eq('id', id)
      .single();
  },

  // Create new accident report (user submission)
  create: async (data, userId = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const accidentData = {
      accident_date: data.accident_date,
      accident_time: data.accident_time || null,
      parish: data.parish,
      city: data.city || null,
      location_description: data.location_description,
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      accident_type: data.accident_type || 'collision',
      vehicles_involved: data.vehicles_involved || [],
      fatality_count: data.fatality_count || 1,
      injury_count: data.injury_count || 0,
      road_name: data.road_name || null,
      road_type: data.road_type || null,
      source_url: data.source_url,
      source_name: data.source_name || 'User Submission',
      source_date: data.source_date || null,
      summary: data.summary || null,
      submitted_by: userId,
      is_verified: false
    };

    const { data: accident, error } = await supabase
      .from('road_accidents')
      .insert(accidentData)
      .select()
      .single();

    if (error) return { data: null, error };

    // Add victims if provided
    if (data.victims && data.victims.length > 0) {
      const victimsData = data.victims.map(v => ({
        accident_id: accident.id,
        age: v.age || null,
        age_group: v.age_group || getAgeGroup(v.age),
        gender: v.gender || null,
        role: v.role || 'unknown',
        parish_of_origin: v.parish_of_origin || null
      }));
      await supabase.from('road_accident_victims').insert(victimsData);
    }

    return { data: accident, error: null };
  },

  // Admin: Verify accident
  verify: async (id, reviewerId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('road_accidents')
      .update({
        is_verified: true,
        reviewed_by: reviewerId,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
  },

  // Admin: Reject accident
  reject: async (id, reviewerId, reason = '') => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('road_accidents')
      .update({
        is_verified: false,
        reviewed_by: reviewerId,
        reviewed_at: new Date().toISOString(),
        rejection_reason: reason
      })
      .eq('id', id)
      .select()
      .single();
  },

  // Admin: Update accident
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('road_accidents')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete accident
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // First delete victims
    await supabase.from('road_accident_victims').delete().eq('accident_id', id);
    return await supabase.from('road_accidents').delete().eq('id', id);
  },

  // Admin: Create pre-verified accident (bypasses pending review)
  createAdmin: async (data, adminId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const accidentData = {
      accident_date: data.accident_date,
      accident_time: data.accident_time || null,
      parish: data.parish,
      city: data.city || null,
      location_description: data.location_description || null,
      road_name: data.road_name || null,
      road_type: data.road_type || null,
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      accident_type: data.accident_type || 'collision',
      vehicles_involved: data.vehicles_involved || [],
      fatality_count: data.fatality_count || 1,
      injury_count: data.injury_count || 0,
      source_url: data.source_url || null,
      source_name: data.source_name || 'Admin Entry',
      source_date: data.source_date || null,
      summary: data.summary || null,
      submitted_by: adminId,
      is_verified: true,
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString()
    };

    const { data: accident, error } = await supabase
      .from('road_accidents')
      .insert(accidentData)
      .select()
      .single();

    if (error) return { data: null, error };

    // Add victims if provided
    if (data.victims && data.victims.length > 0) {
      const victimsData = data.victims.map(v => ({
        accident_id: accident.id,
        age: v.age || null,
        age_group: v.age_group || getAgeGroup(v.age),
        gender: v.gender || null,
        role: v.role || 'unknown',
        parish_of_origin: v.parish_of_origin || null
      }));
      await supabase.from('road_accident_victims').insert(victimsData);
    }

    return { data: accident, error: null };
  },

  // Admin: Bulk import accidents from CSV/JSON data
  bulkImport: async (records, adminId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' }, imported: 0, failed: 0 };

    let imported = 0;
    let failed = 0;
    const errors = [];

    for (const record of records) {
      try {
        const result = await roadFatalities.createAdmin(record, adminId);
        if (result.error) {
          failed++;
          errors.push({ record, error: result.error.message });
        } else {
          imported++;
        }
      } catch (err) {
        failed++;
        errors.push({ record, error: err.message });
      }
    }

    return { imported, failed, errors, error: failed > 0 ? { message: `${failed} records failed to import` } : null };
  },

  // Update victim
  updateVictim: async (victimId, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('road_accident_victims')
      .update({
        age: data.age || null,
        age_group: data.age_group || getAgeGroup(data.age),
        gender: data.gender || null,
        role: data.role || 'unknown',
        parish_of_origin: data.parish_of_origin || null
      })
      .eq('id', victimId)
      .select()
      .single();
  },

  // Add victim to existing accident
  addVictim: async (accidentId, victimData) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('road_accident_victims')
      .insert({
        accident_id: accidentId,
        age: victimData.age || null,
        age_group: victimData.age_group || getAgeGroup(victimData.age),
        gender: victimData.gender || null,
        role: victimData.role || 'unknown',
        parish_of_origin: victimData.parish_of_origin || null
      })
      .select()
      .single();
  },

  // Delete victim
  deleteVictim: async (victimId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('road_accident_victims').delete().eq('id', victimId);
  },

  // Get statistics
  getStats: async (filters = {}) => {
    if (!supabase) return { data: null, error: null };
    let query = supabase
      .from('road_accidents')
      .select('id, parish, accident_date, fatality_count, injury_count, vehicles_involved, victims:road_accident_victims(age_group, gender, role)')
      .eq('is_verified', true);

    if (filters.year) query = query.gte('accident_date', `${filters.year}-01-01`).lte('accident_date', `${filters.year}-12-31`);
    if (filters.parish) query = query.eq('parish', filters.parish);

    const { data, error } = await query;
    if (error) return { data: null, error };

    // Calculate stats
    const stats = {
      totalAccidents: data.length,
      totalFatalities: data.reduce((sum, a) => sum + (a.fatality_count || 0), 0),
      totalInjuries: data.reduce((sum, a) => sum + (a.injury_count || 0), 0),
      byParish: {},
      byMonth: {},
      byVehicleType: {},
      byVictimAge: {},
      byVictimGender: {},
      byVictimRole: {}
    };

    data.forEach(accident => {
      // By parish
      stats.byParish[accident.parish] = (stats.byParish[accident.parish] || 0) + (accident.fatality_count || 0);

      // By month
      const month = accident.accident_date?.substring(0, 7);
      if (month) stats.byMonth[month] = (stats.byMonth[month] || 0) + (accident.fatality_count || 0);

      // By vehicle type
      (accident.vehicles_involved || []).forEach(v => {
        stats.byVehicleType[v] = (stats.byVehicleType[v] || 0) + 1;
      });

      // By victim demographics
      (accident.victims || []).forEach(v => {
        if (v.age_group) stats.byVictimAge[v.age_group] = (stats.byVictimAge[v.age_group] || 0) + 1;
        if (v.gender) stats.byVictimGender[v.gender] = (stats.byVictimGender[v.gender] || 0) + 1;
        if (v.role) stats.byVictimRole[v.role] = (stats.byVictimRole[v.role] || 0) + 1;
      });
    });

    return { data: stats, error: null };
  },

  // Get pending submissions count
  getPendingCount: async () => {
    if (!supabase) return { count: 0, error: null };
    const { count, error } = await supabase
      .from('road_accidents')
      .select('*', { count: 'exact', head: true })
      .eq('is_verified', false)
      .is('reviewed_at', null);
    return { count: count || 0, error };
  },

  // Add victim to existing accident
  addVictim: async (accidentId, victimData) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('road_accident_victims')
      .insert({
        accident_id: accidentId,
        age: victimData.age || null,
        age_group: victimData.age_group || getAgeGroup(victimData.age),
        gender: victimData.gender || null,
        role: victimData.role || 'unknown',
        parish_of_origin: victimData.parish_of_origin || null
      })
      .select()
      .single();
  },

  // Update victim
  updateVictim: async (victimId, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('road_accident_victims')
      .update(data)
      .eq('id', victimId)
      .select()
      .single();
  },

  // Delete victim
  deleteVictim: async (victimId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('road_accident_victims').delete().eq('id', victimId);
  }
};

// Contact form submissions
export const contactSubmissions = {
  // Submit a contact form
  submit: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const result = await supabase
      .from('contact_submissions')
      .insert({
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        subject: data.subject,
        category: data.category || 'general',
        message: data.message,
        status: 'new',
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    // The admin notification is created server-side by the
    // on_contact_submission trigger (see supabase/schema-notifications.sql),
    // consistent with every other notification type. No client-side insert is
    // needed here — doing so would create a duplicate notification.

    return result;
  },

  // Admin: Get all submissions
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }
    return await query;
  },

  // Admin: Update submission status
  updateStatus: async (id, status, adminNotes = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = {
      status,
      updated_at: new Date().toISOString()
    };
    if (adminNotes !== null) updates.admin_notes = adminNotes;
    return await supabase
      .from('contact_submissions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
  },

  // Admin: Delete submission
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('contact_submissions').delete().eq('id', id);
  },

  // Get count of new submissions (for admin badge)
  getNewCount: async () => {
    if (!supabase) return { count: 0, error: null };
    const { count, error } = await supabase
      .from('contact_submissions')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'new');
    return { count: count || 0, error };
  }
};

// Helper function to determine age group
const getAgeGroup = (age) => {
  if (!age || age < 0) return 'unknown';
  if (age < 13) return 'child';
  if (age < 20) return 'teen';
  if (age < 40) return 'young_adult';
  if (age < 60) return 'adult';
  return 'senior';
};

export default supabase;
